import React, { useState, useContext, useEffect } from 'react';
import { FieldData, ConditionLinesDefaultsRowsFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { v4 as uuidv4 } from 'uuid';
import FormFieldContext from '../FormFieldContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { ConditionLineType, createNewConditionLine, serializeConditionLines } from '../../../views/settings/ConditionLineTypes';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

const ConditionLinesDefaultsRowsField: React.FC<ConditionLinesDefaultsRowsFieldType & { data: FieldData, viewKey: string }> = ({
    name, label, tooltipText, data
}) => {
    const { t } = useTranslation();
    const { errorMessages } = useGlobalContext();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const [rows, setRows] = useState<ConditionLineType[]>([]);

    // Load the existing condition lines from the fetched data and give every row a client uuid
    useEffect(() => {
        const existingRows = Array.isArray(data?.[name]) ? data[name] : [];
        setRows(existingRows.map((row: ConditionLineType) => ({ ...row, uuid: row.uuid || uuidv4() })));
    }, [data]);

    // Update the rows state and the updated data without the client uuids
    const syncUpdatedData = (updatedRows: ConditionLineType[]) => {
        setRows(updatedRows);
        setUpdatedData((current: any) => ({ ...current, [name]: serializeConditionLines(updatedRows) }));
    };

    // Update the label or value of a row
    const updateRow = (uuid: string, changes: Partial<ConditionLineType>) => {
        syncUpdatedData(rows.map(row => row.uuid === uuid ? { ...row, ...changes } : row));
    };

    // Add a new empty row at the end
    const handleAddRow = () => {
        syncUpdatedData([...rows, createNewConditionLine(uuidv4())]);
    };

    // Remove a row from the list
    const handleDeleteRow = (uuid: string) => {
        syncUpdatedData(rows.filter(row => row.uuid !== uuid));
    };

    // Get the error message of this field
    const errorMessage = errorMessages?.[name];

    return (
        <div className='rows-field condition-lines-field'>
            {label &&
                <label>
                    {t(label)}
                    {tooltipText &&
                        <span className="tooltip-icon">
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className="tooltip tooltip-right">{t(tooltipText)}</span>
                        </span>
                    }
                </label>
            }
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing && (
                <div className='edit-mode'>
                    {rows.map(row => (
                        <div className='condition-line-row' key={row.uuid}>
                            <input
                                type='text'
                                id={`condition_label_${row.uuid}`}
                                name={`condition_label_${row.uuid}`}
                                value={row.label}
                                placeholder={t('workflow.general.condition_lines_label_placeholder')}
                                onChange={event => updateRow(row.uuid as string, { label: event.target.value })}
                                data-1p-ignore='true'
                            />
                            <input
                                type='text'
                                id={`condition_value_${row.uuid}`}
                                name={`condition_value_${row.uuid}`}
                                value={row.value}
                                placeholder={t('workflow.general.condition_lines_value_placeholder')}
                                onChange={event => updateRow(row.uuid as string, { value: event.target.value })}
                                data-1p-ignore='true'
                            />
                            <div className='delete-icon tooltip-icon'>
                                <FontAwesomeIcon icon={faTrash} onClick={() => handleDeleteRow(row.uuid as string)} />
                                <span className="tooltip">{t('general.delete')}</span>
                            </div>
                        </div>
                    ))}
                    {errorMessage &&
                        <div className='error-message'>
                            {t(errorMessage, { defaultValue: errorMessage })}
                        </div>
                    }
                    <div onClick={(e) => { e.preventDefault(); handleAddRow(); }}
                        className="add-new-button">
                        {t('workflow.general.condition_lines_add')}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConditionLinesDefaultsRowsField;
